import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import css from './AppPage.module.scss';
import { PageHeader } from './PageHeader';

/**
 * Display title of the flagship moment-search page. Doubles as the `header` prop
 * value AND a logic key here (it drives the gradient background + `isSearchActive`),
 * and it's the nav rail's label for the same destination — so it lives in one place
 * to keep those in sync. Renaming the page = change this one const.
 */
export const MOMENTS_HEADER = 'Moments';

const AppPageContent = observer(function AppPageContent({
  children,
  header,
  noUser = false,
  gradientBackground = false,
  floatingHeader = false,
  ...props
}: {
  children: React.ReactNode;
  header: React.ReactNode;
  noUser?: boolean;
  gradientBackground?: boolean;
  /** Drop the title and pin the header chrome (upload / notifs) so it floats over
   *  the content instead of reserving a header row — the content then gets the
   *  full vertical space. */
  floatingHeader?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  const libraryManager = useLibraryManager();
  const isSearchActive = header === MOMENTS_HEADER && libraryManager.multiLibSearch.hasSearchTerms;

  return (
    <div
      {...props}
      className={classNames(
        css.appPage,
        header === 'Workspace' ||
          (header === MOMENTS_HEADER && !isSearchActive) ||
          gradientBackground
          ? css.workspaceSearchBackground
          : css.defaultBackground,
        floatingHeader && css.floatingHeaderLayout,
        props.className,
      )}
    >
      <PageHeader noUser={noUser} floating={floatingHeader}>
        {header}
      </PageHeader>
      <div className={css.content}>{children}</div>
    </div>
  );
});

export const AppPage = observer(function AppPage({
  children,
  header,
  noUser = false,
  gradientBackground = false,
  floatingHeader = false,
  ...props
}: {
  children: React.ReactNode;
  header: React.ReactNode;
  noUser?: boolean;
  gradientBackground?: boolean;
  floatingHeader?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <AppPageContent
      header={header}
      noUser={noUser}
      gradientBackground={gradientBackground}
      floatingHeader={floatingHeader}
      {...props}
    >
      {children}
    </AppPageContent>
  );
});
